import { useState } from 'react'
import type { ApiKeyCreated } from '../types/api'
import { formatDate } from '../lib/format'

interface ApiKeySecretModalProps {
  apiKey: ApiKeyCreated
  onClose: () => void
}

export default function ApiKeySecretModal({ apiKey, onClose }: ApiKeySecretModalProps) {
  const [copied, setCopied] = useState<'id' | 'secret' | null>(null)

  const copyToClipboard = async (text: string, type: 'id' | 'secret') => {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(type)
      setTimeout(() => setCopied(null), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Backdrop */}
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />

        {/* Modal */}
        <div className="relative bg-white rounded-xl shadow-modal w-full max-w-lg p-6">
          <div className="mb-4">
            <div className="flex items-center justify-center w-12 h-12 mx-auto bg-green-100 rounded-full mb-4">
              <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 text-center">
              API Key Created Successfully
            </h3>
            <p className="mt-2 text-sm text-gray-500 text-center">
              <strong className="text-red-600">Important:</strong> Save the client secret now!
              You won't be able to see it again.
            </p>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Display Name
              </label>
              <p className="text-sm text-gray-900">{apiKey.display_name}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Client ID
              </label>
              <div className="flex items-center space-x-2">
                <code className="flex-1 text-sm bg-gray-100 px-3 py-2 rounded-lg font-mono break-all">
                  {apiKey.client_id}
                </code>
                <button
                  onClick={() => copyToClipboard(apiKey.client_id, 'id')}
                  className="flex-shrink-0 px-3 py-2 text-sm font-medium text-accent bg-accent/10 rounded-lg hover:bg-accent/20 transition-colors"
                >
                  {copied === 'id' ? 'Copied!' : 'Copy'}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Client Secret
              </label>
              <div className="flex items-center space-x-2">
                <code className="flex-1 text-sm bg-yellow-50 px-3 py-2 rounded-lg font-mono break-all border border-yellow-200">
                  {apiKey.client_secret}
                </code>
                <button
                  onClick={() => copyToClipboard(apiKey.client_secret, 'secret')}
                  className="flex-shrink-0 px-3 py-2 text-sm font-medium text-accent bg-accent/10 rounded-lg hover:bg-accent/20 transition-colors"
                >
                  {copied === 'secret' ? 'Copied!' : 'Copy'}
                </button>
              </div>
              <p className="mt-1 text-xs text-red-600">
                ⚠️ This secret will not be shown again. Please copy it now!
              </p>
              <p className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                Use this key on one installation only. If two share it, the first to rotate it leaves the other
                with a secret that stops working on {formatDate(apiKey.expires_at)}.
              </p>
            </div>

            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="text-sm font-medium text-gray-900 mb-2">Usage</h4>
              <p className="text-xs text-gray-600 mb-2">
                Use the OAuth2 client credentials flow to obtain an access token:
              </p>
              {apiKey.target_apps && apiKey.target_apps.length > 1 && (
                <p className="text-xs text-gray-500 mb-3">
                  Your API key has access to multiple APIs. Use the appropriate scope for the API you want to call:
                </p>
              )}
              {(apiKey.target_apps && apiKey.target_apps.length > 0 ? apiKey.target_apps : [{ name: 'default', display_name: '', scope: apiKey.scope, roles: [] }]).map((app) => (
                <div key={app.name} className={apiKey.target_apps && apiKey.target_apps.length > 1 ? "bg-gray-100 rounded p-3 mb-3 last:mb-0" : ""}>
                  {app.display_name && (
                    <p className="text-xs font-medium text-gray-700 mb-1">{app.display_name}</p>
                  )}
                  <pre className="text-xs bg-gray-100 px-3 py-2 rounded block overflow-x-auto whitespace-pre-wrap">
                    {`POST ${apiKey.token_endpoint}
Content-Type: application/x-www-form-urlencoded

grant_type=client_credentials
&client_id=${apiKey.client_id}
&client_secret=YOUR_SECRET
&scope=${app.scope}`}
                  </pre>
                  {app.roles.length > 0 && (
                    <p className="text-xs text-gray-500 mt-1">
                      Roles: {app.roles.join(', ')}
                    </p>
                  )}
                </div>
              ))}
              <p className="text-xs text-gray-500 mt-2">
                The returned JWT access token should be included as a Bearer token in API requests.
              </p>
            </div>
          </div>

          <div className="mt-6">
            <button
              onClick={onClose}
              className="w-full px-4 py-2.5 text-sm font-medium text-white bg-accent border border-transparent rounded-lg hover:bg-accent-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent transition-colors"
            >
              I've saved the secret
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
